import DeleteModal from "@via/components/delete-modal/delete-modal";
import Drawer from "@via/components/drawer";
import {
  BannerSize,
  BannerType,
  MessageBanner,
} from "@via/components/message-banner/message-banner";
import Modal from "@via/components/modal";
import { DrawerSize } from "@via/components/table/table";
import { useAuth } from "@via/hooks/useAuth";
import { useVmetaCanvasOwner } from "@via/hooks/useVmetaCanvasOwner";
import LoginDrawerForm from "@via/pages/projects/metadata/configuration/data-import/components/login-drawer-form";
import LoginList, {
  KeyExpiryCell,
} from "@via/pages/projects/metadata/configuration/data-import/components/login-list";
import {
  useDeleteImportConfig,
  useImportConfigs,
  useImportLogins,
} from "@via/pages/projects/metadata/configuration/data-import/data-import.api.tsx";
import ImportConfigForm from "@via/pages/projects/metadata/configuration/data-import/import-config-form";
import {
  intervalLabel,
  platformLabel,
} from "@via/pages/projects/metadata/configuration/data-import/utils/form";
import { useVmetaCanvas } from "@via/pages/projects/projects.api.tsx";
import {
  EMPTY_TABLE_MESSAGE,
  METADATA_CONFIGURATION_TABS,
  USER_ROLES,
} from "@via/utils/constants";
import { formatDateString } from "@via/utils/helpers";
import type {
  VmetaImportConfigListItem,
  VmetaImportLogin,
  VmetaImportPlatform,
} from "common";
import { FC, useState } from "react";

interface DataImportProps {
  projectId: string;
  activeTab?: string;
}

const dateText = (iso: string | null) =>
  iso ? formatDateString(new Date(iso), "LLL") : "";

/** The bound credential's name (from the person's list, else the config's own name), with its expiry. */
const CredentialCell: FC<{
  item: VmetaImportConfigListItem;
  logins: VmetaImportLogin[];
}> = ({ item, logins }) => {
  if (item.credentialId === null) return <>Not set</>;
  const found = logins.find(l => l.id === item.credentialId);
  return (
    <>
      <span>
        {found?.name ?? item.credentialName ?? "Credential unavailable"}
      </span>
      {found && (
        <div>
          <KeyExpiryCell
            platform={found.platform}
            keyExpiresAt={found.keyExpiresAt}
          />
        </div>
      )}
    </>
  );
};

const DataImport: FC<DataImportProps> = ({ projectId, activeTab }) => {
  const { user } = useAuth();
  const isAdmin =
    !!user && typeof user === "object" && user.userRole === USER_ROLES.ADMIN;
  const { data: canvasData, isLoading: isCanvasLoading } =
    useVmetaCanvas(projectId);
  const isOwner = useVmetaCanvasOwner(canvasData?.data?.owner);
  const canWrite = isAdmin || !!canvasData?.data?.writeAllowed || isOwner;
  const isActive =
    activeTab === undefined ||
    activeTab === METADATA_CONFIGURATION_TABS.DATA_IMPORT;

  const { data: configs, isLoading } = useImportConfigs(
    projectId,
    canWrite && isActive
  );
  const { data: logins, isLoading: isLoadingLogins } = useImportLogins(
    canWrite && isActive
  );
  const { mutateAsync: deleteConfig, isPending: isDeleting } =
    useDeleteImportConfig();
  const [loginEditing, setLoginEditing] = useState<{
    login: VmetaImportLogin | null;
    platform: VmetaImportPlatform;
  } | null>(null);
  const [choosingLogin, setChoosingLogin] = useState(false);

  const [editing, setEditing] = useState<{
    configId: string | null;
    platform: VmetaImportPlatform;
    focusRunLog?: boolean;
  } | null>(null);
  const [summary, setSummary] = useState<VmetaImportConfigListItem | null>(
    null
  );
  const [choosing, setChoosing] = useState(false);
  const [toDelete, setToDelete] = useState<{ id: string; name: string } | null>(
    null
  );

  if (!isCanvasLoading && !canWrite) {
    return (
      <div className="mt-4">
        <MessageBanner
          messages="You need write permission on this project to manage its data imports."
          type={BannerType.Info}
          size={BannerSize.Small}
        />
      </div>
    );
  }

  const open = (item: VmetaImportConfigListItem, focusRunLog = false) => {
    if (item.editable)
      setEditing({
        configId: item._id,
        platform: item.platform as VmetaImportPlatform,
        focusRunLog,
      });
    else setSummary(item);
  };

  return (
    <>
      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={() => setChoosing(true)}
          className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
          New import
        </button>
      </div>
      <div className="mt-4 overflow-auto">
        <table className="min-w-full divide-y divide-gray-300 text-sm">
          <thead>
            <tr className="text-left font-semibold text-gray-900">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Platform</th>
              <th className="px-3 py-2">Enabled</th>
              <th className="px-3 py-2">Interval</th>
              <th className="px-3 py-2">Last run</th>
              <th className="px-3 py-2">Credential</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-gray-500">
                  Loading data imports
                </td>
              </tr>
            )}
            {!isLoading && (configs ?? []).length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-4 text-gray-500">
                  {EMPTY_TABLE_MESSAGE.IMPORT_CONFIGS}
                </td>
              </tr>
            )}
            {(configs ?? []).map(item => (
              <tr key={item._id} aria-label={item.name}>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    className="via-link text-left"
                    onClick={() => open(item)}>
                    {item.name}
                  </button>
                </td>
                <td className="px-3 py-2">{platformLabel(item.platform)}</td>
                <td className="px-3 py-2">{item.autoUpdate ? "On" : "Off"}</td>
                <td className="px-3 py-2">
                  {intervalLabel(item.autoUpdateInterval)}
                </td>
                <td className="px-3 py-2">
                  {item.lastRun ? (
                    <button
                      type="button"
                      className="via-link text-left"
                      onClick={() => open(item, true)}>
                      {`${item.lastRun.status} ${dateText(item.lastRun.dateEnded)}`}
                    </button>
                  ) : (
                    "Never"
                  )}
                </td>
                <td className="px-3 py-2">
                  <CredentialCell item={item} logins={logins?.items ?? []} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <LoginList
        items={logins?.items ?? []}
        isLoading={isLoadingLogins}
        onOpen={item =>
          setLoginEditing({ login: item, platform: item.platform })
        }
        onNew={() => setChoosingLogin(true)}
      />

      <Modal
        open={choosingLogin}
        setOpen={setChoosingLogin}
        title="New credential"
        showButtons={false}>
        <div className="flex gap-3 p-2">
          {(["freezerpro", "mydatahelps"] as const).map(platform => (
            <button
              key={platform}
              type="button"
              onClick={() => {
                setChoosingLogin(false);
                setLoginEditing({ login: null, platform });
              }}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              {platformLabel(platform)}
            </button>
          ))}
        </div>
      </Modal>

      <Drawer
        id="import-login-editor"
        open={!!loginEditing}
        setOpen={() => setLoginEditing(null)}
        size={DrawerSize.Big}
        title={loginEditing?.login ? "Edit credential" : "New credential"}>
        {loginEditing ? (
          <LoginDrawerForm
            login={loginEditing.login}
            platform={loginEditing.platform}
            organizations={logins?.organizations ?? []}
            onClose={() => setLoginEditing(null)}
          />
        ) : undefined}
      </Drawer>

      <Modal
        open={choosing}
        setOpen={setChoosing}
        title="New import"
        showButtons={false}>
        <div className="flex gap-3 p-2">
          {(["freezerpro", "mydatahelps"] as const).map(platform => (
            <button
              key={platform}
              type="button"
              onClick={() => {
                setChoosing(false);
                setEditing({ configId: null, platform });
              }}
              className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              {platformLabel(platform)}
            </button>
          ))}
        </div>
      </Modal>

      <Drawer
        id="import-config-editor"
        open={!!editing}
        setOpen={() => setEditing(null)}
        size={DrawerSize.Big}
        title={editing?.configId ? "Edit data import" : "New data import"}>
        {editing ? (
          <ImportConfigForm
            canvasId={projectId}
            configId={editing.configId}
            platform={editing.platform}
            projectName={canvasData?.data?.name}
            focusRunLog={editing.focusRunLog}
            onClose={() => setEditing(null)}
            onDelete={setToDelete}
          />
        ) : undefined}
      </Drawer>

      <Drawer
        id="import-config-summary"
        open={!!summary}
        setOpen={() => setSummary(null)}
        size={DrawerSize.Big}
        title="Data import">
        {summary ? (
          <div className="space-y-3 p-4 text-sm">
            <p className="font-semibold">{summary.name}</p>
            <p>Platform: {platformLabel(summary.platform)}</p>
            <p>
              The editor supports FreezerPro and MyDataHelps imports. This one
              can only be deleted, which removes it and anything stored with it
              for good.
            </p>
            <button
              type="button"
              onClick={() =>
                setToDelete({ id: summary._id, name: summary.name })
              }
              className="rounded-md bg-red-600 px-3 py-2 font-semibold text-white hover:bg-red-500">
              Delete this import
            </button>
          </div>
        ) : undefined}
      </Drawer>

      <DeleteModal
        item={toDelete}
        setItem={setToDelete}
        deleteFunction={async () => {
          await deleteConfig({ canvasId: projectId, id: toDelete?.id ?? "" });
          setSummary(null);
          setEditing(null);
        }}
        isLoading={isDeleting}
        title="Delete data import"
        confirmText="Delete import"
      />
    </>
  );
};

export default DataImport;
